/**
 * Draws the Path Details trace-route graph using ciena-graphlib / ciena-dagre + d3.
 *
 * Each agent gets its own SVG graph rendered left-to-right:
 *   [Agent] ── [hop nodes …] ── [Destination]
 *
 * Summary mode: hops grouped by organisation (consolidated nodes)
 * Detailed mode: one node per hop
 */
import _ from 'lodash';
import { curveMonotoneX, line, select } from 'd3';
import { layout } from 'ciena-dagre';
import graphLib from 'ciena-graphlib';

// ── Constants ────────────────────────────────────────────────────────────────

const LABEL_HEIGHT = 14;
const NODE_RADIUS = 18;
const DEST_WIDTH = 26;

const AGENT_RECT_W = 17;
const AGENT_RECT_H = 32;
const AGENT_DOT_R = 1.8;
const AGENT_DOT_GAP = 7;

const GRAPH_CONFIG = {
    rankdir: 'LR',
    ranker: 'tight-tree',
    ranksep: 90,
    nodesep: 60,
    marginx: 30,
    marginy: 40,
};

const NODE_DEFAULTS = { width: 1, height: 1 };

// ── Hop helpers (re-use logic from WebQuickTestsResults) ─────────────────────

function getHopIp(hop) {
    return hop?.ip || hop?.ipAddress || hop?.ip_address || hop?.addr || '';
}

function getHopHost(hop) {
    return hop?.hostname || hop?.host || hop?.dns || hop?.resolved_dns || hop?.resolved_host || '';
}

// Mirrors portal/ui `getOrganizationName(geoIp)` in plm/portal/api/lib/tests/traceroute/graphHelper.js:
// geoIp.provider → geoIp.organization → 'Unknown'. Hops in WTN have GeoIP fields
// merged directly onto them by the API-side parseHops, so the same priority applies.
function getOrgTitle(hop) {
    return hop?.provider || hop?.organization || hop?.as_org || hop?.isp || 'Unknown';
}

// Matches portal/ui `networkGraph.helper.js`:
//   text((d) => getOrganizationName(geoIP).substr(0, 3))
// i.e. the first 3 characters of the organization name as-is (so 'Unknown' → 'Unk').
function shortOrgLabel(name) {
    if (!name) return '';
    return String(name).substr(0, 3);
}

function parseLatencyMsValue(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number.parseFloat(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
}

function getHopLatencyMs(hop) {
    if (!hop) return null;
    const responseTimes = Array.isArray(hop?.response_times)
        ? hop.response_times.map(parseLatencyMsValue).filter((v) => v !== null)
        : [];
    if (responseTimes.length) return Math.max(...responseTimes);

    const candidates = [
        hop?.maxLatency,
        hop?.latency,
        hop?.rtt,
        hop?.response_time,
        hop?.responseTime,
        hop?.roundTripTime,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
        const parsed = parseLatencyMsValue(candidates[i]);
        if (parsed !== null) return parsed;
    }
    return null;
}

// Matches portal/ui `networkGraph.helper.js`:
//   maxLatency === 0 ? '<1 ms' : `${maxLatency} ms`
// (raw number, no rounding).
function formatLatencyLabel(ms) {
    if (ms === null || ms === undefined) return '';
    if (ms === 0) return '<1 ms';
    return `${ms} ms`;
}

function getTraceHops(traceRoute) {
    if (!traceRoute) return [];
    if (Array.isArray(traceRoute.hops)) return traceRoute.hops;
    if (Array.isArray(traceRoute.hop_data)) return traceRoute.hop_data;
    if (Array.isArray(traceRoute?.trace_route?.route?.hops)) return traceRoute.trace_route.route.hops;
    if (Array.isArray(traceRoute?.route?.hops)) return traceRoute.route.hops;
    if (Array.isArray(traceRoute?.traceRoute?.route?.hops)) return traceRoute.traceRoute.route.hops;
    if (Array.isArray(traceRoute.path)) return traceRoute.path;
    return [];
}

// ── Build graph model ────────────────────────────────────────────────────────

/**
 * Builds summary-mode nodes mirroring portal/ui's `addToConsolidatedPath`
 * (plm/portal/api/lib/tests/traceroute/graphHelper.js):
 *   - Responsive hops with the same org merge into the previous group.
 *   - Unresponsive hops merge into the previous consolidated group regardless of org.
 *   - A leading unresponsive run becomes its own 'Unknown' group (portal/ui calls
 *     `pushNewConsolidatedNode` with UNKNOWN_ORG_NAME when no previous node exists),
 *     instead of being prepended to the first responsive group.
 */
function buildSummaryNodes(hops) {
    const grouped = [];

    hops.forEach((hop) => {
        const hasIp = Boolean(getHopIp(hop));
        const last = grouped[grouped.length - 1];

        if (!hasIp) {
            // Unresponsive: merge into previous consolidated node if any,
            // otherwise start a new 'Unknown' group (matches portal/ui).
            if (last) {
                last.hops.push(hop);
            } else {
                grouped.push({ title: 'Unknown', short: shortOrgLabel('Unknown'), hops: [hop] });
            }
            return;
        }

        const title = getOrgTitle(hop);
        if (last && last.title === title) {
            last.hops.push(hop);
        } else {
            grouped.push({ title, short: shortOrgLabel(title), hops: [hop] });
        }
    });

    // Isolate the very last hop into its own node so it gets the destination icon.
    // Mirrors portal/ui's `getNodeType` returning Server for the final hop and
    // `addToConsolidatedPath` routing Server through `addToPath` (separate node)
    // rather than into the consolidated org group.
    if (grouped.length) {
        const last = grouped[grouped.length - 1];
        if (last.hops.length > 1) {
            const beforeLast = { ...last, hops: last.hops.slice(0, -1) };
            const onlyLast = { ...last, hops: [last.hops[last.hops.length - 1]] };
            return [...grouped.slice(0, -1), beforeLast, onlyLast];
        }
    }
    return grouped;
}

/**
 * Builds detailed-mode nodes: one node per hop.
 */
function buildDetailedNodes(hops) {
    return hops.map((hop) => {
        const title = getOrgTitle(hop);
        return { title, short: shortOrgLabel(title), hops: [hop] };
    });
}

const AGENT_LABEL_TAG_PREFIX = {
    pulseName: 'nPoint Name',
    responseTime: 'Response Time',
    organization: 'Organization',
    location: 'nPoint Location',
    coordinates: 'Coordinates',
};

function renderServerAgentLabels(labels) {
    return (labels || [])
        .filter(Boolean)
        .map((label) => {
            if (typeof label === 'string') return label;
            if (label && label.tag && label.data != null) {
                const prefix = AGENT_LABEL_TAG_PREFIX[label.tag] || label.tag;
                return `${prefix}: ${label.data}`;
            }
            return null;
        })
        .filter(Boolean);
}

function buildAgentNode(agent, agentsById, extraSublabel = null, serverAgentNode = null) {
    const agentId = agent.agent;
    const agentInfo = agentsById?.[agentId];
    const agentName = agentInfo?.name || agentId || 'Agent';

    if (serverAgentNode && Array.isArray(serverAgentNode.labels) && serverAgentNode.labels.length) {
        const lines = renderServerAgentLabels(serverAgentNode.labels);
        if (extraSublabel) lines.push(extraSublabel);
        const [first, ...rest] = lines;
        const agentLabelLines = lines.length;
        const agentNodeHeight = Math.max(AGENT_RECT_H, (agentLabelLines + 1) * LABEL_HEIGHT - GRAPH_CONFIG.nodesep);
        return {
            id: `agent-${agentId}`,
            nodeType: 'agent',
            label: first || '',
            sublabel: rest.join('\n'),
            sources: [agentId],
            width: AGENT_RECT_W + 12,
            height: agentNodeHeight,
        };
    }

    const runResponses = Array.isArray(agent?.results)
        ? agent.results.map((r) => r?.data?.avgResponse).filter((v) => typeof v === 'number' && Number.isFinite(v))
        : [];
    let responseMs = null;
    if (runResponses.length) {
        responseMs = Math.max(...runResponses);
    } else if (typeof agent?.rollUp?.avgRtMs === 'number') {
        responseMs = agent.rollUp.avgRtMs;
    }
    const responseText = responseMs != null ? `${Math.round(responseMs)} ms` : '-- ms';

    // GeoIP for the nPoint itself (portal/ui looks this up from agent.network.ipv4Local).
    // In React the WTN page pre-loads `agentsById[id]` from /ipm/v1/open/agents/:_id/location,
    // so use it directly — do NOT fall back to traceRoute.geoData[0] which is the FIRST HOP's geo.
    const geo = agentInfo || {};
    const organization = geo.provider || geo.organization || geo.isp || geo.as_org || '';
    const city = geo.city || '';
    const lat = geo.latitude;
    const lon = geo.longitude;
    const coordStr = lat != null && lon != null ? `(${lat},${lon})` : '';

    const sublabelLines = [`Response Time: ${responseText}`];
    if (organization) sublabelLines.push(`Organization: ${organization}`);
    if (city) sublabelLines.push(`nPoint Location: ${city}`);
    if (coordStr) sublabelLines.push(`Coordinates: ${coordStr}`);
    if (extraSublabel) sublabelLines.push(extraSublabel);

    // Size the agent node so two stacked agents end up with exactly one
    // blank line of vertical gap between their descriptions. dagre puts
    // centers (h + nodesep) apart, and the label spans
    // `agentLabelLines * LABEL_HEIGHT` vertically; one extra LABEL_HEIGHT
    // is the desired gap.
    const agentLabelLines = 1 + sublabelLines.length;
    const agentNodeHeight = Math.max(AGENT_RECT_H, (agentLabelLines + 1) * LABEL_HEIGHT - GRAPH_CONFIG.nodesep);

    return {
        id: `agent-${agentId}`,
        nodeType: 'agent',
        label: `nPoint Name: ${agentName}`,
        sublabel: sublabelLines.join('\n'),
        sources: [agentId],
        width: AGENT_RECT_W + 12,
        height: agentNodeHeight,
    };
}

/**
 * Builds a single trace-route graph that merges multiple agents.
 *
 * Nodes shared across agents are de-duplicated:
 *   - Detailed mode: hops merge by IP address
 *   - Summary mode: hops merge by organization name
 *   - Destination merges by destination IP
 *
 * Unknown / unresponsive hops stay distinct per agent (they have no stable
 * identifier to merge on). Agent nodes are always distinct per agent.
 *
 * Edges merge by (from, to); their `sources` lists are unioned and the
 * displayed latency uses the max across agents.
 *
 * See `plm/portal/ui/lib/networkGraph` + `plm/portal/api/lib/tests/traceroute/graphHelper.js`
 * for the original portal/ui implementation that this mirrors.
 */
function buildMergedGraphModel(agents, agentsById, detailLevel) {
    const nodes = [];
    const edges = [];
    const nodeMap = new Map();
    const edgeMap = new Map();

    // Build a single geoData lookup by IP across all agents.
    const geoDataByIp = {};
    (agents || []).forEach((agent) => {
        const list = _.get(agent, 'traceRoute.geoData', []) || [];
        list.forEach((g) => {
            if (g?.ip && !geoDataByIp[g.ip]) geoDataByIp[g.ip] = g;
        });
    });

    function upsertNode(node) {
        const existing = nodeMap.get(node.id);
        if (!existing) {
            // attach the shared geo lookup so the tooltip code can resolve IPs
            // regardless of which agent originally produced the hop.
            nodeMap.set(node.id, { ...node, geoDataByIp });
            nodes.push(nodeMap.get(node.id));
            return nodeMap.get(node.id);
        }
        existing.sources = _.uniq([...(existing.sources || []), ...(node.sources || [])]);
        if (existing.pathNode && node.pathNode) {
            const seen = new Set(existing.pathNode.hops);
            node.pathNode.hops.forEach((h) => {
                if (!seen.has(h)) {
                    existing.pathNode.hops.push(h);
                    seen.add(h);
                }
            });
        } else if (node.pathNode && !existing.pathNode) {
            existing.pathNode = node.pathNode;
        }
        // Destination wins over hop/unknown if any agent reached it here.
        if (node.nodeType === 'destination') {
            existing.nodeType = 'destination';
            if (node.labelLines) existing.labelLines = node.labelLines;
            if (node.label) existing.label = node.label;
        }
        return existing;
    }

    function upsertEdge(edge) {
        const key = `${edge.from}\u0000${edge.to}`;
        const existing = edgeMap.get(key);
        if (!existing) {
            edgeMap.set(key, { ...edge });
            edges.push(edgeMap.get(key));
            return edgeMap.get(key);
        }
        existing.sources = _.uniq([...(existing.sources || []), ...(edge.sources || [])]);
        const latencies = [existing.latencyMs, edge.latencyMs].filter((v) => v != null);
        existing.latencyMs = latencies.length ? Math.max(...latencies) : null;
        if (!existing.latencyLabel && edge.latencyLabel) {
            existing.latencyLabel = edge.latencyLabel;
        } else if (existing.latencyMs != null && existing.latencyLabel) {
            existing.latencyLabel = formatLatencyLabel(existing.latencyMs);
        }
        return existing;
    }

    (agents || []).forEach((agent) => {
        const agentId = agent.agent;
        const traceRoute = agent.traceRoute || {};
        const hops = Array.isArray(agent.traceHops) ? agent.traceHops : getTraceHops(traceRoute);
        const hasError = Boolean(traceRoute.error && String(traceRoute.error).trim());
        const noHopsReason = hops.length === 0 && hasError ? 'Trace route is unavailable' : null;

        const agentNode = buildAgentNode(agent, agentsById, noHopsReason);
        const agentNodeId = agentNode.id;
        upsertNode(agentNode);

        if (!hops.length) return;

        const pathNodes = detailLevel === 'detailed' ? buildDetailedNodes(hops) : buildSummaryNodes(hops);
        if (!pathNodes.length) return;

        let prevId = agentNodeId;
        pathNodes.forEach((pn, idx) => {
            const isLast = idx === pathNodes.length - 1;
            const firstHop = pn.hops[0];
            const lastHop = pn.hops[pn.hops.length - 1];
            const hopIp = getHopIp(firstHop);
            const lastHopIp = getHopIp(lastHop);
            const lastHopHost = getHopHost(lastHop);
            const isUnknown = !hopIp;

            // Stable ID so that the same node merges across agents.
            //   destination → keyed by destination IP
            //   detailed     → keyed by hop IP
            //   summary      → keyed by organization title
            //   unresponsive → keyed per-agent so unknown hops stay distinct
            let nodeId;
            if (isLast && lastHopIp) {
                nodeId = `dest-${lastHopIp}`;
            } else if (isLast) {
                nodeId = `dest-${agentId}-${idx}`;
            } else if (isUnknown) {
                nodeId = `unknown-${agentId}-${idx}`;
            } else if (detailLevel === 'detailed') {
                nodeId = `hop-${hopIp}`;
            } else {
                nodeId = `org-${pn.title}`;
            }

            let nodeType = 'hop';
            if (isLast) nodeType = 'destination';
            else if (isUnknown) nodeType = 'unknown';

            const destLabelLines = isLast ? [lastHopHost, lastHopIp].filter(Boolean) : null;

            upsertNode({
                id: nodeId,
                nodeType,
                label: isLast ? destLabelLines.join('\n') || pn.short : pn.short,
                labelLines: destLabelLines,
                pathNode: { ...pn, hops: [...pn.hops] },
                isUnknown,
                sources: [agentId],
            });

            const edgeLatency = getHopLatencyMs(firstHop);
            // portal/ui shows latency on every edge (graphHelper sets maxLatency on every edge);
            // keep parity instead of hiding labels on summary intermediate edges.
            const showLabel = true;
            upsertEdge({
                from: prevId,
                to: nodeId,
                latencyMs: edgeLatency,
                latencyLabel: showLabel ? formatLatencyLabel(edgeLatency) : '',
                sources: [agentId],
            });

            prevId = nodeId;
        });
    });

    return { nodes, edges };
}

// Server `graphHelper` node-type constants — keep in sync with
// plm/portal/api/lib/tests/traceroute/graphHelper.js (NODETYPES).
const SERVER_NODE_TYPE = {
    NETWORK_DEVICE: 'Network Device',
    UNKNOWN: 'Unknown',
    NPOINT: 'nPoint',
    SERVER: 'Server',
    CONSOLIDATED: 'Organization',
};

/**
 * Translates a hop object produced server-side (graphHelper `getHopData`):
 *   {hopNumber, ip, name, geoIp, responseTimes:[ms,...]}
 * into the shape our tooltip / latency helpers expect on the React side
 * (response_times as `${n}ms` strings, geoIp fields flattened on the hop).
 */
function serverHopToReactHop(srvHop) {
    if (!srvHop) return {};
    const geoIp = srvHop.geoIp || {};
    // Server-side `addToPath` stores DNS name only inside `labels: [name, ip]`
    // (no top-level `name` field on individual hop nodes), so fall back to
    // labels[0] when the hop came from a translated server graph node.
    const labels = Array.isArray(srvHop.labels) ? srvHop.labels : [];
    const hostnameFromLabels = labels.find((l) => typeof l === 'string' && l && l !== srvHop.ip);
    return {
        ip: srvHop.ip,
        hostname: srvHop.name || hostnameFromLabels,
        response_times: Array.isArray(srvHop.responseTimes) ? srvHop.responseTimes.map((v) => `${v}ms`) : [],
        geoIp,
        // Flattened copies so getHopLatencyMs / tooltip / getOrgTitle work uniformly.
        provider: geoIp.provider,
        organization: geoIp.organization,
        city: geoIp.city,
        latitude: geoIp.latitude,
        longitude: geoIp.longitude,
        isoCode: geoIp.isoCode,
    };
}

/**
 * Builds the React graph model from server-built `graphData` instead of
 * recomputing topology client-side. Used when `test.graphData` is available
 * so React renders the SAME merged/consolidated topology that portal/ui shows.
 *
 * Agent blocks (icon + multi-line label + stacking) are still constructed on
 * the React side via `buildAgentNode` to preserve the bespoke layout; only
 * the hop nodes and their edges come from the server.
 *
 * @param {Object} serverGraph  One of `quickTestDoc.graphData.consolidated`
 *                              / `quickTestDoc.graphData.detailed` (from
 *                              `plm/portal/api/lib/tests/traceroute/graphHelper.js`).
 * @param {Array}  agents       Normalised agent results from React side.
 * @param {Object} agentsById   Agent-id → agent-info lookup.
 */
function buildModelFromServerGraph(serverGraph, agents, agentsById) {
    const nodes = [];
    const edges = [];
    const nodeMap = new Map();
    const serverNodeById = new Map();

    (serverGraph?.nodes || []).forEach((n) => serverNodeById.set(n.id, n));

    // Geo lookup by IP for the tooltip code (mirrors the lookup built by
    // buildMergedGraphModel from per-agent traceRoute.geoData).
    const geoDataByIp = {};
    (serverGraph?.nodes || []).forEach((n) => {
        if (n?.ip && n.geoIp && !geoDataByIp[n.ip]) geoDataByIp[n.ip] = n.geoIp;
    });

    function ensure(node) {
        if (!nodeMap.has(node.id)) {
            const withGeo = { ...node, geoDataByIp };
            nodeMap.set(node.id, withGeo);
            nodes.push(withGeo);
        }
        return nodeMap.get(node.id);
    }

    // 1. Build agent blocks (React side) so the visual layout matches the
    //    rest of the app. Skip the server-side nPoint node entirely.
    const agentIds = new Set();
    (agents || []).forEach((agent) => {
        if (!agent?.agent) return;
        const traceRoute = agent.traceRoute || {};
        const hasError = Boolean(traceRoute.error && String(traceRoute.error).trim());
        const serverAgentNode = serverNodeById.get(agent.agent);
        const hasServerHops = (serverGraph?.edges || []).some((e) => e.from === agent.agent);
        const extra = !hasServerHops && hasError ? 'Trace route is unavailable' : null;
        ensure(buildAgentNode(agent, agentsById, extra, serverAgentNode));
        agentIds.add(agent.agent);
        // Mark server agent node as consumed; we don't translate it.
        if (serverAgentNode) serverAgentNode._consumed = true;
    });

    // 2. Translate hop nodes (everything except nPoint).
    (serverGraph?.nodes || []).forEach((sn) => {
        if (sn.type === SERVER_NODE_TYPE.NPOINT) return;

        const isServer = sn.type === SERVER_NODE_TYPE.SERVER;
        const isUnknown = sn.type === SERVER_NODE_TYPE.UNKNOWN;
        const isConsolidated = sn.type === SERVER_NODE_TYPE.CONSOLIDATED;

        let nodeType = 'hop';
        if (isServer) nodeType = 'destination';
        else if (isUnknown) nodeType = 'unknown';

        let pathNode;
        let label;
        let labelLines = null;

        if (isConsolidated) {
            const title = sn.organization || 'Unknown';
            const hops = (sn.hops || []).map(serverHopToReactHop);
            pathNode = { title, short: shortOrgLabel(title), hops };
            label = pathNode.short;
        } else {
            const hop = serverHopToReactHop(sn);
            const title = isUnknown ? 'Unknown' : getOrgTitle(hop);
            pathNode = { title, short: shortOrgLabel(title), hops: [hop] };
            if (isServer) {
                labelLines = [hop.hostname, hop.ip].filter(Boolean);
                label = labelLines.join('\n') || pathNode.short;
            } else {
                label = pathNode.short;
            }
        }

        ensure({
            id: `srv-${sn.id}`,
            nodeType,
            label,
            labelLines,
            pathNode,
            isUnknown,
            sources: Array.isArray(sn.sources) ? [...sn.sources] : [],
        });
    });

    // 3. Translate edges, remapping nPoint endpoints onto our React agent ids.
    function mapEndpoint(serverId) {
        if (agentIds.has(serverId) || serverNodeById.get(serverId)?.type === SERVER_NODE_TYPE.NPOINT) {
            return `agent-${serverId}`;
        }
        return `srv-${serverId}`;
    }

    (serverGraph?.edges || []).forEach((e) => {
        if (!nodeMap.has(mapEndpoint(e.from)) || !nodeMap.has(mapEndpoint(e.to))) return;
        const latencyMs = typeof e.maxLatency === 'number' && Number.isFinite(e.maxLatency) ? e.maxLatency : null;
        edges.push({
            from: mapEndpoint(e.from),
            to: mapEndpoint(e.to),
            latencyMs,
            latencyLabel: formatLatencyLabel(latencyMs),
            sources: Array.isArray(e.sources) ? [...e.sources] : [],
        });
    });

    return { nodes, edges };
}

// ── D3 drawing ───────────────────────────────────────────────────────────────

function doLayout(graph) {
    const originNode = 'ORIGIN_NODE';
    const destinationNode = 'DESTINATION_NODE';
    graph.setNode(originNode);
    graph.setNode(destinationNode);

    graph.sources().forEach((source) => graph.setEdge(originNode, source, { weight: 200 }));
    graph.sinks().forEach((sink) => graph.setEdge(sink, destinationNode, { weight: 100 }));

    layout(graph);

    graph.outEdges(originNode).forEach((e) => graph.removeEdge(e.v, e.w));
    graph.inEdges(destinationNode).forEach((e) => graph.removeEdge(e.v, e.w));
    graph.removeNode(originNode);
    graph.removeNode(destinationNode);

    // ── Agent post-layout adjustments ────────────────────────────────────
    // 1) Align every agent on the same x (icons line up vertically).
    // 2) Re-space them vertically so each agent's full multi-line description
    //    is separated from its neighbour by exactly one blank text line.
    //    dagre's `nodesep` is unreliable here because multiple agents often
    //    converge onto the same shared hop, which compresses the source rank.
    const agentNodes = graph
        .nodes()
        .map((id) => ({ id, n: graph.node(id) }))
        .filter(({ n }) => n && n.nodeType === 'agent');

    if (agentNodes.length > 1) {
        const alignedX = Math.min(...agentNodes.map(({ n }) => n.x));

        // Sort by current y to preserve dagre's chosen ordering.
        agentNodes.sort((a, b) => a.n.y - b.n.y);

        // Label vertical extent for an agent: lines drawn at
        //   cy - LABEL_HEIGHT, cy, cy + LABEL_HEIGHT, ... cy + (n - 2) * LABEL_HEIGHT
        // with a bit of ascent above and descent below the text baselines.
        const ASCENT = 10;
        const DESCENT = 5;
        const GAP = LABEL_HEIGHT; // one blank line between agents

        const labelLineCount = (n) => {
            const labelLines = (n.label || '').split('\n').length;
            const subLines = n.sublabel ? n.sublabel.split('\n').length : 0;
            return Math.max(1, labelLines + subLines);
        };
        const labelTopFromCenter = LABEL_HEIGHT + ASCENT;
        const labelBottomFromCenter = (n) => {
            const lines = labelLineCount(n);
            return (lines - 2) * LABEL_HEIGHT + DESCENT;
        };

        const firstY = agentNodes[0].n.y;
        // Anchor first agent where dagre placed it, then walk down stacking
        // each subsequent agent at: previousBottom + GAP + currentTop.
        let cursorBottom = firstY + labelBottomFromCenter(agentNodes[0].n);
        const newYById = { [agentNodes[0].id]: firstY };

        for (let i = 1; i < agentNodes.length; i += 1) {
            const node = agentNodes[i].n;
            const newY = cursorBottom + GAP + labelTopFromCenter;
            newYById[agentNodes[i].id] = newY;
            cursorBottom = newY + labelBottomFromCenter(node);
        }

        agentNodes.forEach(({ id, n }) => {
            n.x = alignedX;
            n.y = newYById[id];
        });

        // Keep edge start points attached to the re-positioned agent icons.
        graph.edges().forEach((e) => {
            const src = graph.node(e.v);
            const edge = graph.edge(e);
            if (src && src.nodeType === 'agent' && edge && Array.isArray(edge.points) && edge.points.length) {
                edge.points[0] = { x: src.x, y: src.y };
            }
        });

        // Expand the overall graph height so the new agent y values fit.
        const graphMeta = graph.graph();
        if (graphMeta) {
            const lastBottom = cursorBottom + DESCENT + (graphMeta.marginy || 0);
            graphMeta.height = Math.max(graphMeta.height || 0, lastBottom);
        }
    }
}

function drawEdges(svgGroup, graph) {
    const lineFunction = line()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(curveMonotoneX);

    svgGroup
        .selectAll('path.graph-edge')
        .data(graph.edges())
        .enter()
        .append('path')
        .classed('graph-edge', true)
        .attr('d', (d) => lineFunction(graph.edge(d).points));

    // Edge labels (latency)
    ['edge-label-outline', 'edge-label'].forEach((cls) => {
        svgGroup
            .selectAll(`text.${cls}`)
            .data(graph.edges())
            .enter()
            .append('text')
            .classed(cls, true)
            .attr('x', (d) => {
                const pts = graph.edge(d).points;
                return pts[Math.floor(pts.length / 2)].x;
            })
            .attr('y', (d) => {
                const pts = graph.edge(d).points;
                return pts[Math.floor(pts.length / 2)].y + 4;
            })
            .attr('text-anchor', 'middle')
            .text((d) => graph.edge(d).latencyLabel || '');
    });
}

function drawNodes(svgGroup, graph, detailLevel, onNodeClick) {
    const graphNodes = svgGroup
        .selectAll('g.graph-node')
        .data(graph.nodes())
        .enter()
        .append('g')
        .classed('graph-node', true);

    graphNodes.each(function renderNode(d) {
        const nodeGroup = select(this);
        const nodeData = graph.node(d);
        const cx = nodeData.x;
        const cy = nodeData.y;

        // ── Shape ──
        if (nodeData.nodeType === 'agent') {
            // Rounded rect with 3 indicator dots
            nodeGroup
                .append('rect')
                .classed('agent-icon', true)
                .attr('x', cx - AGENT_RECT_W / 2)
                .attr('y', cy - AGENT_RECT_H / 2)
                .attr('width', AGENT_RECT_W)
                .attr('height', AGENT_RECT_H)
                .attr('rx', 3)
                .attr('ry', 3);
            [-1, 0, 1].forEach((i) => {
                nodeGroup
                    .append('circle')
                    .classed('agent-dot', true)
                    .attr('cx', cx)
                    .attr('cy', cy + i * AGENT_DOT_GAP)
                    .attr('r', AGENT_DOT_R);
            });
        } else if (nodeData.nodeType === 'destination') {
            nodeGroup
                .append('rect')
                .classed('dest-hit-area', true)
                .attr('x', cx - DEST_WIDTH / 2)
                .attr('y', cy - DEST_WIDTH / 2)
                .attr('width', DEST_WIDTH)
                .attr('height', DEST_WIDTH)
                .attr('fill', 'transparent');
            // Material-icons 'storage' glyph rendered like portal/ui networkGraph:
            // two stacked <text> layers — 'dest-outline' (dark stroke) under
            // 'dest-icon' (light fill) — with no rectangle background.
            ['dest-outline', 'dest-icon'].forEach((cls) => {
                nodeGroup
                    .append('text')
                    .classed(`material-icons ${cls}`, true)
                    .attr('x', cx)
                    .attr('y', cy)
                    .attr('dominant-baseline', 'central')
                    .attr('text-anchor', 'middle')
                    .text('storage');
            });
        } else if (nodeData.isUnknown) {
            nodeGroup
                .append('circle')
                .classed('unknown-circle', true)
                .attr('cx', cx)
                .attr('cy', cy)
                .attr('r', NODE_RADIUS);
        } else {
            nodeGroup.append('circle').classed('hop-circle', true).attr('cx', cx).attr('cy', cy).attr('r', NODE_RADIUS);
            // Short label inside circle
            if (nodeData.label) {
                nodeGroup
                    .append('text')
                    .classed('hop-inner-label', true)
                    .attr('x', cx)
                    .attr('y', cy)
                    .attr('dominant-baseline', 'central')
                    .attr('text-anchor', 'middle')
                    .text(nodeData.label);
            }
        }

        // ── Labels below node ──
        if (nodeData.nodeType === 'agent') {
            const lines = (nodeData.label || '').split('\n').concat((nodeData.sublabel || '').split('\n'));
            // Anchor the label a comfortable distance left of the agent rect
            // so the text doesn't visually butt up against the icon.
            const labelX = cx - AGENT_RECT_W / 2 - 10;
            const textEl = nodeGroup
                .append('text')
                .classed('agent-label', true)
                .attr('text-anchor', 'end')
                .attr('x', labelX)
                .attr('y', cy - LABEL_HEIGHT);
            lines.forEach((ln, i) => {
                textEl
                    .append('tspan')
                    .attr('x', labelX)
                    .attr('dy', i === 0 ? 0 : LABEL_HEIGHT)
                    .text(ln);
            });
        } else if (nodeData.nodeType === 'destination') {
            const lines =
                Array.isArray(nodeData.labelLines) && nodeData.labelLines.length
                    ? nodeData.labelLines
                    : [nodeData.label || ''];
            const startX = cx + DEST_WIDTH / 2 + 6;
            const startY = cy + 4 - ((lines.length - 1) * LABEL_HEIGHT) / 2;
            const textEl = nodeGroup
                .append('text')
                .classed('dest-label', true)
                .attr('text-anchor', 'start')
                .attr('x', startX)
                .attr('y', startY);
            lines.forEach((ln, i) => {
                textEl
                    .append('tspan')
                    .attr('x', startX)
                    .attr('dy', i === 0 ? 0 : LABEL_HEIGHT)
                    .text(ln);
            });
        }

        if (nodeData.nodeType === 'agent') {
            const agentTooltip = [...(nodeData.label || '').split('\n'), ...(nodeData.sublabel || '').split('\n')]
                .filter((ln) => ln && ln.trim())
                .join('\n');
            if (agentTooltip) nodeData.tooltipText = agentTooltip;
        }

        // ── Tooltip (summary) ──
        if (nodeData.pathNode) {
            const pn = nodeData.pathNode;
            const tooltipLines = [];
            if (detailLevel === 'detailed') {
                const hop = pn.hops[0];
                const hopIp = getHopIp(hop);

                if (!hopIp) {
                    tooltipLines.push('No response');
                } else {
                    const geoIp = hop?.geoIp || hop?.geo_ip || {};
                    const geoLookup = nodeData.geoDataByIp && hopIp ? nodeData.geoDataByIp[hopIp] || {} : {};
                    [
                        geoIp.provider ?? geoLookup.provider ?? hop?.provider ?? hop?.isp,
                        geoIp.organization ?? geoLookup.organization ?? hop?.organization ?? hop?.as_org,
                        geoIp.city ?? geoLookup.city ?? hop?.city ?? hop?.location?.city,
                        geoIp.isoCode ?? geoLookup.isoCode ?? hop?.isoCode ?? hop?.location?.isoCode,
                    ].forEach((v) => {
                        if (v) tooltipLines.push(v);
                    });

                    const lat = geoIp.latitude ?? geoLookup.latitude ?? hop?.latitude ?? hop?.lat;
                    const lon = geoIp.longitude ?? geoLookup.longitude ?? hop?.longitude ?? hop?.lon;
                    if (lat != null && lon != null) tooltipLines.push(`Coordinates: (${lat},${lon})`);

                    const dns = getHopHost(hop);
                    tooltipLines.push(dns || '');

                    tooltipLines.push(hopIp);
                }
            } else {
                tooltipLines.push(`Owner: ${pn.title}`);
                tooltipLines.push(`${pn.hops.length} hops:`);
                pn.hops.forEach((hop) => {
                    const ip = getHopIp(hop);
                    if (ip) {
                        const host = getHopHost(hop);
                        tooltipLines.push(host ? `${ip} (${host})` : ip);
                    } else {
                        tooltipLines.push('Unresponsive Hop');
                    }
                });
            }
            const tooltipText = tooltipLines.join('\n');
            if (nodeData.nodeType !== 'destination') {
                nodeGroup.append('title').text(tooltipText);
            }
            nodeData.tooltipText = tooltipText;
        }

        // ── Click → copy to clipboard ──
        if (onNodeClick) {
            nodeGroup.style('cursor', 'pointer').on('click', () => onNodeClick(nodeData));
        }
    });

    // ── Hover highlighting: dim everything not belonging to the same agent(s)
    // as the hovered node. Edges and nodes both carry a `sources` list of
    // agent ids; dim anything whose sources don't intersect the hovered set.
    const DIM_OPACITY = 0.15;
    const intersects = (a, b) => {
        if (!a || !b || !a.length || !b.length) return false;
        for (let i = 0; i < a.length; i += 1) {
            if (b.indexOf(a[i]) !== -1) return true;
        }
        return false;
    };
    const applyHighlight = (sources) => {
        svgGroup.selectAll('g.graph-node').style('opacity', (id) => {
            const n = graph.node(id);
            return intersects(n?.sources, sources) ? 1 : DIM_OPACITY;
        });
        const edgeOpacity = (e) => {
            const ed = graph.edge(e);
            return intersects(ed?.sources, sources) ? 1 : DIM_OPACITY;
        };
        svgGroup.selectAll('path.graph-edge').style('opacity', edgeOpacity);
        svgGroup.selectAll('text.edge-label').style('opacity', edgeOpacity);
        svgGroup.selectAll('text.edge-label-outline').style('opacity', edgeOpacity);
    };
    const clearHighlight = () => {
        svgGroup.selectAll('g.graph-node').style('opacity', null);
        svgGroup.selectAll('path.graph-edge').style('opacity', null);
        svgGroup.selectAll('text.edge-label').style('opacity', null);
        svgGroup.selectAll('text.edge-label-outline').style('opacity', null);
    };
    graphNodes
        .on('mouseenter', function onEnter(d) {
            const n = graph.node(typeof d === 'string' ? d : select(this).datum());
            applyHighlight(n?.sources || []);
        })
        .on('mouseleave', clearHighlight);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Draws a merged trace-route graph for ALL agents into the given SVG element.
 *
 * When `graphData` (the server-built `quickTestDoc.graphData` from
 * `plm/portal/api/lib/tests/traceroute/graphHelper.js`) is supplied, the hop
 * topology is taken from it directly so React renders the same merged graph
 * that portal/ui shows. Otherwise the topology is rebuilt client-side from raw
 * `traceRoute.hop_data` via `buildMergedGraphModel`.
 *
 * Agent nodes (icon + multi-line label + stacking) are always constructed on
 * the React side so the visual layout stays consistent.
 *
 * @param {SVGElement} svgElement    The <svg> to draw into (will be cleared first).
 * @param {Array}      agents        Normalised agent results from WebQuickTestsResults.
 * @param {Object}     agentsById    Agent-id → agent-info lookup.
 * @param {string}     detailLevel   'summary' | 'detailed'
 * @param {Function}   [onNodeClick] Optional click callback receiving the node data.
 * @param {Object}     [graphData]   Optional server-built graph
 *                                   ({consolidated, detailed} with {nodes, edges, paths}).
 */
export default function drawPathDetailGraph(svgElement, agents, agentsById, detailLevel, onNodeClick, graphData) {
    const rootSvg = select(svgElement);
    rootSvg.selectAll('*').remove();

    const serverGraph = graphData && graphData[detailLevel === 'detailed' ? 'detailed' : 'consolidated'];
    const { nodes, edges } =
        serverGraph && Array.isArray(serverGraph.nodes) && serverGraph.nodes.length
            ? buildModelFromServerGraph(serverGraph, agents, agentsById)
            : buildMergedGraphModel(agents, agentsById, detailLevel);
    if (!nodes.length) return _.noop;

    const graph = new graphLib.Graph().setGraph(GRAPH_CONFIG).setDefaultEdgeLabel(() => ({ label: '' }));

    nodes.forEach((node) => {
        graph.setNode(node.id, _.defaults(node, NODE_DEFAULTS));
    });

    edges.forEach((edge) => {
        graph.setEdge(edge.from, edge.to, edge);
    });

    doLayout(graph);

    const svgGroup = rootSvg.append('g');
    drawEdges(svgGroup, graph);
    drawNodes(svgGroup, graph, detailLevel, onNodeClick);

    const graphHeight = graph.graph()?.height || 120;
    const graphWidth = graph.graph()?.width || 400;

    // Agent label is anchored to the left of the agent node and can extend
    // into negative coordinates, which clips on first render. Measure the
    // actual content bbox and shift the group so nothing is cut off.
    const PAD = 12;
    let bbox = { x: 0, y: 0, width: graphWidth, height: graphHeight };
    try {
        bbox = svgGroup.node().getBBox();
    } catch (e) {
        // getBBox can throw if the SVG isn't yet in the DOM; fall back to graph dims.
    }
    const offsetX = Math.max(0, -bbox.x) + PAD;
    const offsetY = Math.max(0, -bbox.y) + PAD;
    svgGroup.attr('transform', `translate(${offsetX}, ${offsetY})`);

    const totalWidth = Math.max(bbox.width + offsetX + PAD, graphWidth + 20, 400);
    const totalHeight = Math.max(bbox.height + offsetY + PAD, graphHeight + 20, 100);
    rootSvg.attr('height', totalHeight).attr('width', totalWidth);
}
